import React from 'react';
import {
  Truck,
  ShieldCheck,
  Clock,
  MapPin,
  Handshake,
  CheckCircle2,
  Target,
  TrendingUp
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, stats, certifications, values } from '../mock';

const iconMap = {
  'shield-check': ShieldCheck,
  clock: Clock,
  'map-pin': MapPin,
  handshake: Handshake,
  truck: Truck
};

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            À propos d'<span className="text-orange-400">InfraOne</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {companyInfo.shortTagline}
          </p>
        </div>
      </section>

      {/* Histoire */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Notre <span className="text-orange-500">Histoire</span>
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  <strong>InfraOne</strong> est née d'une conviction simple :
                  les industriels gabonais — mines, agro-industrie, exploitation
                  forestière, logistique portuaire — méritent un partenaire
                  logistique aussi exigeant qu'eux.
                </p>
                <p>
                  Nous avons construit notre flotte autour de cette exigence :
                  des camions et engins lourds modernes, entretenus dans notre
                  atelier intégré, et des équipages formés aux contraintes
                  spécifiques du terrain gabonais (pistes minières, saisons
                  pluvieuses, convois exceptionnels).
                </p>
                <p>
                  Aujourd'hui, plus de 50 engins sillonnent quotidiennement les
                  axes Moanda – Owendo, Franceville – Port-Gentil et l'ensemble
                  du territoire. Chaque rotation est suivie GPS, chaque tonne
                  comptabilisée, chaque incident analysé — pour une amélioration
                  continue de notre service.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className="bg-orange-50 border-orange-200"
                >
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-sm text-slate-700">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos <span className="text-orange-500">Valeurs</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Quatre principes non négociables, présents dans chaque mission.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = iconMap[value.icon] || ShieldCheck;
              return (
                <Card
                  key={index}
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon size={32} className="text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-t-4 border-t-orange-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Target size={28} className="text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Notre Mission
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Sécuriser la chaîne logistique de nos clients industriels en
                  apportant fiabilité, traçabilité et capacité de mobilisation.
                  Faire en sorte que chaque tonne livrée le soit dans les
                  délais, à coût maîtrisé, et sans incident HSE.
                </p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-blue-500">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp size={28} className="text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Notre Vision
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Devenir l'opérateur logistique de référence pour les
                  industries lourdes en Afrique Centrale. Numériser nos
                  opérations, électrifier progressivement notre flotte et
                  élever le standard du métier au Gabon.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Zones d'intervention */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Zones d'<span className="text-orange-500">intervention</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une couverture nationale sur les principaux corridors miniers,
              agricoles et portuaires.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {companyInfo.zonesIntervention.map((zone, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <MapPin size={20} className="text-orange-500 mt-1 flex-shrink-0" />
                <span className="text-slate-700 font-medium">{zone}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Certifications & <span className="text-orange-400">Agréments</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Notre engagement qualité et HSE reconnu par des normes
              internationales.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-slate-800 p-6 rounded-lg hover:bg-slate-700 transition-colors duration-300"
              >
                <CheckCircle2 size={32} className="text-orange-400 mb-3" />
                <p className="text-white font-medium text-sm">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
